// Libraries
import { Table, Popup, TableFilter, ViewAuditTrails } from '../../components';
import { fetchAuditTrailStaticRequest } from '../../redux/common/staticData/staticData.actions';
import { fetchAuditTrailRequest } from './redux/auditTrail.actions';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

// Layouts

// Components

// Actions

// Styles
import './auditTrail.scss';

const filterFieldList = [
    {
        fieldType: 'date',
        label: 'Created On',
        name: 'createdOn',
        props: {},
    },
    {
        fieldType: 'time',
        label: 'Time',
        name: 'time',
        props: {
            hour24Mode: true,
        },
    },
    {
        fieldType: 'input',
        label: 'User Id',
        name: 'userId',
        props: {
            placeholder: 'Enter User Id',
        },
    },
    {
        fieldType: 'select',
        label: 'Event',
        name: 'action',
        props: {
            data: [],
            label: 'Select',
            labelField: 'name',
            valueField: 'value',
        },
    },
    {
        fieldType: 'input',
        label: 'Resources',
        name: 'resource',
        props: {
            placeholder: 'Enter Resource',
        },
    },
];

const setActionFilters = (fieldsList, staticEvents) => {
    if (staticEvents && staticEvents.filters) {
        const objectKeys = Object.keys(staticEvents.filters);
        const result = objectKeys.map(element => {
            return {
                name: staticEvents.filters[element],
                value: element,
            };
        });

        fieldsList.forEach(element => {
            if (element.name === 'action') {
                element.props.data = result;
            }
        });
        return fieldsList;
    }
    return fieldsList;
};

const AuditTrailActions = ({ data, fetchData, resetPage }) => {
    const [showFilter, setShowFilter] = useState(false);
    const [actionType, setActionType] = useState(null);
    const handleOpenActionPopup = actionType => {
        setShowFilter(true);
        setActionType(actionType);
    };

    const handleCloseOpenActionPopup = isFetch => {
        setShowFilter(false);
        setActionType(null);
        if (isFetch && !isFetch.target) {
            fetchData({
                pageIndex: 1,
                pageSize: 10,
            });
            resetPage();
        }
    };

    return (
        <div className="userAction multiItem">
            <button className="view" onClick={handleOpenActionPopup} />
            <Popup
                title={data.original.resource}
                actionType={actionType}
                showCloseIcon
                showOverlay
                showFilter={showFilter}
                data={data.original}
                closePopup={handleCloseOpenActionPopup}
            >
                <ViewAuditTrails
                    actionType={actionType}
                    data={data.original}
                    handleOpenActionPopup={handleOpenActionPopup}
                    closePopup={handleCloseOpenActionPopup}
                />
            </Popup>
        </div>
    );
};

const AuditTrail = props => {
    const {
        auditTrailData,
        actions,
        loader: { showLoader },
        staticData,
    } = props;

    const { error, tableData, isLoading, pagination } = auditTrailData;
    const { staticEvents } = staticData;

    useEffect(() => {
        if (isEmpty(staticEvents)) {
            actions.fetchAuditTrailStaticRequest();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns = useMemo(
        () => [
            { header: 'Date & Time', accessor: 'dateAndTime' },
            { header: 'User ID', accessor: 'userId' },
            { header: 'Event', accessor: 'event' },
            { header: 'IP Address', accessor: 'externalIpAddress' },
            { header: 'Resources', accessor: 'resource' },
            { header: 'Status', accessor: 'status' },
            { header: 'Action', accessor: 'action' },
        ],
        []
    );

    const pageSizeOptions = [10, 20, 40, 60];

    // We'll start our table without any data
    const [pageCount, setPageCount] = useState(0);
    const [isResetPage, setIsResetPage] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [filterData, setFilterData] = useState({});

    const fetchData = useCallback(
        ({ pageSize, pageIndex }) => {
            // This will get called when the table needs new data
            // You could fetch your data from literally anywhere,
            // even a server. But for this example, we'll just fake it.
            const request = {
                pageSize,
                pageNo: pageIndex,
            };
            actions.fetchAuditTrailRequest({ ...request, ...filterData });
            setPageSize(pageSize);
        },
        [actions, filterData]
    );

    useEffect(() => {
        setPageCount(Math.ceil(pagination.totalNumberOfRecords / pageSize));
    }, [pageSize, pagination.totalNumberOfRecords]);

    const filters = setActionFilters(filterFieldList, staticEvents);

    return (
        <div className="audit-trail-wrapper">
            <Table
                tableTitle="Audit Report"
                filterActionTitle=""
                isFiltersEnable
                columns={columns}
                isPaginationEnable
                data={tableData}
                filterData={filterData}
                error={error && error.errorMessage}
                loading={showLoader || isLoading}
                pageCount={pageCount}
                fetchData={fetchData}
                numberOfRecord={pagination.totalNumberOfRecords}
                pageSizeOptions={pageSizeOptions}
                paginationLabel="Rows per page"
                pagePerView={3}
                isResetPage={isResetPage}
                afterResetPage={() => setIsResetPage(false)}
                userActions={data => (
                    <AuditTrailActions
                        data={data}
                        fetchData={fetchData}
                        resetPage={() => setIsResetPage(true)}
                    />
                )}
                tableFilters={
                    <TableFilter
                        customFilterClass="rightSkewed"
                        showOverlay
                        fieldsList={filters}
                        onSubmit={filterValues => {
                            setFilterData(filterValues);
                            setIsResetPage(true);
                        }}
                        onReset={resetValue => {
                            setFilterData(resetValue);
                            setIsResetPage(true);
                        }}
                    />
                }
            />
        </div>
    );
};

const mapStateToProps = ({ auth, auditTrailData, loader, staticData }) => ({
    auth,
    auditTrailData,
    loader,
    staticData,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            fetchAuditTrailRequest,
            fetchAuditTrailStaticRequest,
        },
        dispatch
    ),
});

AuditTrail.propTypes = {
    actions: PropTypes.object,
    auth: PropTypes.object,
    data: PropTypes.object,
    staticData: PropTypes.object,
    auditTrailData: PropTypes.object,
    loader: PropTypes.object,
};

AuditTrailActions.propTypes = {
    data: PropTypes.object,
    roleStatus: PropTypes.string,
    fetchData: PropTypes.func,
    resetPage: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(AuditTrail);
